import { Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { DisciplineChip } from "@/components/shared/discipline-chip";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { type Discipline, useDisciplines } from "@/hooks/use-disciplines";
import {
  type Composition,
  type PodShape,
  useCreatePodShape,
  usePodShapes,
  useSetTierDefaultShape,
  useTiersWithDefaultShape,
  useUpdatePodShape,
} from "@/hooks/use-pod-shapes";
import { TierScoringDefaultsCard } from "./TierScoringDefaultsCard";

const COMPOSITIONS: Composition[] = ["Solo", "Pair", "Trio", "Quad"];

const POSITION_REQUIREMENTS: Record<Composition, { r2: boolean; r3: boolean; r4: boolean }> = {
  Solo: { r2: false, r3: false, r4: false },
  Pair: { r2: true, r3: false, r4: false },
  Trio: { r2: true, r3: true, r4: false },
  Quad: { r2: true, r3: true, r4: true },
};

export function PodShapeLibrary() {
  const {
    data: shapes = [],
    isLoading,
    isError,
    error,
    refetch,
  } = usePodShapes({ includeInactive: true });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PodShape | null>(null);

  if (isError) {
    return <ErrorState message={error?.message ?? "Failed to load"} onRetry={() => refetch()} />;
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Shapes</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pre-defined Pod compositions — a Shape bundles a Pod size with the Expected Discipline
            per position. Assign a Shape at the Solution level; the Resource Manager then fills the
            positions with Resources. Shape changes do not retroactively change existing Pod
            Positions.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Shape
        </Button>
      </header>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : shapes.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No Shapes defined yet. Create one to get started — e.g. "Standard Trio" with DM / SA / CS.
        </p>
      ) : (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Composition</th>
                <th className="px-5 py-3 font-medium">R1</th>
                <th className="px-5 py-3 font-medium">R2</th>
                <th className="px-5 py-3 font-medium">R3</th>
                <th className="px-5 py-3 font-medium">R4</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shapes.map((s) => (
                <ShapeRow key={s.id} shape={s} onEdit={() => setEditTarget(s)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TierDefaultsCard shapes={shapes} />
      <TierScoringSection />

      <ShapeDialog open={createOpen} onOpenChange={setCreateOpen} shape={null} />
      {editTarget && (
        <ShapeDialog
          open={editTarget !== null}
          onOpenChange={(v) => !v && setEditTarget(null)}
          shape={editTarget}
        />
      )}
    </div>
  );
}

function PositionCell({ code }: { code: string | null }) {
  return (
    <td className="px-5 py-3">
      {code ? <DisciplineChip code={code} /> : <span className="text-muted-foreground">—</span>}
    </td>
  );
}

function ShapeRow({ shape, onEdit }: { shape: PodShape; onEdit: () => void }) {
  const update = useUpdatePodShape();
  return (
    <tr className="border-t">
      <td className="px-5 py-3 font-medium">
        {shape.name}
        {shape.isTierDefaultFor.map((tierName) => (
          <Badge key={tierName} variant="secondary" className="ml-2">
            Default for {tierName}
          </Badge>
        ))}
      </td>
      <td className="px-5 py-3">
        <Badge variant="outline">{shape.composition}</Badge>
      </td>
      <PositionCell code={shape.disciplineR1Code} />
      <PositionCell code={shape.disciplineR2Code} />
      <PositionCell code={shape.disciplineR3Code} />
      <PositionCell code={shape.disciplineR4Code} />
      <td className="px-5 py-3">
        <Badge variant={shape.active ? "default" : "secondary"}>
          {shape.active ? "Active" : "Deprecated"}
        </Badge>
      </td>
      <td className="px-5 py-3 text-right">
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil className="mr-1 h-3.5 w-3.5" />
          Edit
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => update.mutate({ id: shape.id, active: !shape.active })}
          disabled={update.isPending}
        >
          {shape.active ? "Deactivate" : "Reactivate"}
        </Button>
      </td>
    </tr>
  );
}

function TierDefaultsCard({ shapes }: { shapes: PodShape[] }) {
  const { data: tiers = [], isLoading } = useTiersWithDefaultShape();
  const setDefault = useSetTierDefaultShape();
  const activeShapes = shapes.filter((s) => s.active);

  return (
    <section className="space-y-3">
      <div>
        <h3 className="font-display text-lg font-semibold">Tier defaults</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          New Solutions under each Engagement Standard tier start with that tier's default Shape.
          RMs can swap the Shape per Solution with an audit-logged reason.
        </p>
      </div>
      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <tbody>
              {tiers.map((tier) => (
                <tr key={tier.id} className="border-t first:border-t-0">
                  <td className="w-40 px-5 py-3 font-medium">{tier.name}</td>
                  <td className="px-5 py-3">
                    <Select
                      value={tier.defaultShapeId ?? ""}
                      onValueChange={(shapeId) => setDefault.mutate({ tierId: tier.id, shapeId })}
                      disabled={setDefault.isPending}
                    >
                      <SelectTrigger className="max-w-md">
                        {/* No children on SelectValue — React 19 + Radix portals the
                            value node and a text child triggers a createPortal ref error. */}
                        <SelectValue placeholder="No default Shape set" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeShapes.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name} · {s.composition} (
                            {[
                              s.disciplineR1Code,
                              s.disciplineR2Code,
                              s.disciplineR3Code,
                              s.disciplineR4Code,
                            ]
                              .filter(Boolean)
                              .join(" / ")}
                            )
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Fetches the tier list once so the P editor (Story E4) can render independently of shapes. */
function TierScoringSection() {
  const { data: tiers = [], isLoading } = useTiersWithDefaultShape();
  if (isLoading) return <Skeleton className="h-24 w-full" />;
  return <TierScoringDefaultsCard tiers={tiers} />;
}

function ShapeDialog({
  open,
  onOpenChange,
  shape,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  shape: PodShape | null;
}) {
  const { data: disciplines = [] } = useDisciplines();
  const create = useCreatePodShape();
  const update = useUpdatePodShape();

  const [name, setName] = useState(shape?.name ?? "");
  const [composition, setComposition] = useState<Composition>(shape?.composition ?? "Trio");
  const [r1Id, setR1Id] = useState(shape?.disciplineR1Id ?? "");
  const [r2Id, setR2Id] = useState(shape?.disciplineR2Id ?? "");
  const [r3Id, setR3Id] = useState(shape?.disciplineR3Id ?? "");
  const [r4Id, setR4Id] = useState(shape?.disciplineR4Id ?? "");

  const req = POSITION_REQUIREMENTS[composition];
  const compositionLocked = Boolean(shape?.referenced);
  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{shape ? `Edit ${shape.name}` : "New Shape"}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const payload = {
              name,
              composition,
              disciplineR1Id: r1Id,
              disciplineR2Id: req.r2 ? r2Id : null,
              disciplineR3Id: req.r3 ? r3Id : null,
              disciplineR4Id: req.r4 ? r4Id : null,
            };
            if (shape) {
              update.mutate({ id: shape.id, ...payload }, { onSuccess: () => onOpenChange(false) });
            } else {
              create.mutate(payload, { onSuccess: () => onOpenChange(false) });
            }
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="shape-name">Name</Label>
            <Input
              id="shape-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Standard Trio"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Composition</Label>
            <Select
              value={composition}
              onValueChange={(v) => setComposition(v as Composition)}
              disabled={compositionLocked}
            >
              <SelectTrigger
                title={
                  compositionLocked
                    ? "Composition is locked — this Shape is referenced by a Solution, scheduled change, or tier default. Create a new Shape to use a different composition."
                    : undefined
                }
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMPOSITIONS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {compositionLocked && (
              <p className="text-xs text-muted-foreground">
                Composition is locked while this Shape is in use.
              </p>
            )}
          </div>
          <PositionField
            label="R1 Discipline"
            value={r1Id}
            onChange={setR1Id}
            disciplines={disciplines}
          />
          {req.r2 && (
            <PositionField
              label="R2 Discipline"
              value={r2Id}
              onChange={setR2Id}
              disciplines={disciplines}
            />
          )}
          {req.r3 && (
            <PositionField
              label="R3 Discipline"
              value={r3Id}
              onChange={setR3Id}
              disciplines={disciplines}
            />
          )}
          {req.r4 && (
            <PositionField
              label="R4 Discipline"
              value={r4Id}
              onChange={setR4Id}
              disciplines={disciplines}
            />
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? "Saving…" : shape ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PositionField({
  label,
  value,
  onChange,
  disciplines,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disciplines: Discipline[];
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange} required>
        <SelectTrigger>
          <SelectValue placeholder="Select Discipline" />
        </SelectTrigger>
        <SelectContent>
          {disciplines.map((d) => (
            <SelectItem key={d.id} value={d.id}>
              <span className="inline-flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: d.color }}
                  aria-hidden
                />
                {d.code} — {d.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
